/**
 * Médicos Remitentes API Service
 * CRUD operations for referring doctors management
 */

import { api } from "@/lib/api";

const MEDICOS_URL = "/medicos-remitentes/";

// =============================================================================
// CONSTANTS - Especialidades Médicas
// =============================================================================

export const ESPECIALIDADES_MEDICAS = [
  { value: "MEDICINA_GENERAL", label: "Medicina General" },
  { value: "MEDICINA_INTERNA", label: "Medicina Interna" },
  { value: "PEDIATRIA", label: "Pediatría" },
  { value: "GINECOLOGIA", label: "Ginecología y Obstetricia" },
  { value: "CARDIOLOGIA", label: "Cardiología" },
  { value: "DERMATOLOGIA", label: "Dermatología" },
  { value: "ENDOCRINOLOGIA", label: "Endocrinología" },
  { value: "GASTROENTEROLOGIA", label: "Gastroenterología" },
  { value: "GERIATRIA", label: "Geriatría" },
  { value: "HEMATOLOGIA", label: "Hematología" },
  { value: "INFECTOLOGIA", label: "Infectología" },
  { value: "NEFROLOGIA", label: "Nefrología" },
  { value: "NEUMOLOGIA", label: "Neumología" },
  { value: "NEUROLOGIA", label: "Neurología" },
  { value: "NUTRICION", label: "Nutrición" },
  { value: "OFTALMOLOGIA", label: "Oftalmología" },
  { value: "ONCOLOGIA", label: "Oncología" },
  { value: "OTORRINOLARINGOLOGIA", label: "Otorrinolaringología" },
  { value: "PSIQUIATRIA", label: "Psiquiatría" },
  { value: "REUMATOLOGIA", label: "Reumatología" },
  { value: "TRAUMATOLOGIA", label: "Traumatología y Ortopedia" },
  { value: "UROLOGIA", label: "Urología" },
  { value: "CIRUGIA_GENERAL", label: "Cirugía General" },
  { value: "CIRUGIA_PLASTICA", label: "Cirugía Plástica" },
  { value: "CIRUGIA_CARDIOVASCULAR", label: "Cirugía Cardiovascular" },
  { value: "ANESTESIOLOGIA", label: "Anestesiología" },
  { value: "RADIOLOGIA", label: "Radiología" },
  { value: "PATOLOGIA", label: "Patología" },
  { value: "MEDICINA_FAMILIAR", label: "Medicina Familiar" },
  { value: "MEDICINA_DEPORTIVA", label: "Medicina Deportiva" },
  { value: "MEDICINA_OCUPACIONAL", label: "Medicina Ocupacional" },
  { value: "NEONATOLOGIA", label: "Neonatología" },
  { value: "ALERGOLOGIA", label: "Alergología e Inmunología" },
  { value: "OTRA", label: "Otra Especialidad" },
] as const;

export type EspecialidadMedica = (typeof ESPECIALIDADES_MEDICAS)[number]["value"];

/**
 * Get the display label for a specialty code
 */
export function getEspecialidadLabel(code: string | undefined | null): string {
  if (!code) return "";
  const especialidad = ESPECIALIDADES_MEDICAS.find((e) => e.value === code);
  return especialidad?.label || code; // Fallback to code if not found (for legacy data)
}

// =============================================================================
// TYPES
// =============================================================================

export interface MedicoRemitente {
  id: number;
  primer_nombre: string;
  segundo_nombre: string;
  primer_apellido: string;
  segundo_apellido: string;
  nombre_completo: string;
  especialidad: string;
  telefono: string;
  celular: string;
  email: string;
  contacto_principal: string;
  departamento: string;
  ciudad: string;
  clinica_hospital: string;
  ubicacion_completa: string;
  es_tratante_gen: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MedicoRemitenteListItem {
  id: number;
  nombre_completo: string;
  especialidad: string;
  celular: string;
  es_tratante_gen: boolean;
  is_active: boolean;
}

export interface MedicoRemitenteCreateData {
  primer_nombre: string;
  segundo_nombre?: string;
  primer_apellido: string;
  segundo_apellido?: string;
  especialidad?: string;
  telefono?: string;
  celular?: string;
  email?: string;
  departamento?: string;
  ciudad?: string;
  clinica_hospital?: string;
}

export type MedicoRemitenteUpdateData = Partial<MedicoRemitenteCreateData>;

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface MedicoRemitenteFilters {
  search?: string;
  is_active?: boolean;
  especialidad?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Parameters for server-side data table (compatible with useServerDataTable)
 */
export interface FetchParams {
  page: number;
  page_size: number;
  ordering?: string;
  search?: string;
  is_active?: string;
  especialidad?: string;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Get paginated list of médicos remitentes (compatible with useServerDataTable)
 */
export async function getMedicosRemitentes(
  params: FetchParams | MedicoRemitenteFilters = {}
): Promise<PaginatedResponse<MedicoRemitenteListItem>> {
  const searchParams = new URLSearchParams();

  // Handle both FetchParams (from useServerDataTable) and MedicoRemitenteFilters
  if (params.search) searchParams.append("search", String(params.search));
  if (params.ordering) searchParams.append("ordering", String(params.ordering));
  if (params.page) searchParams.append("page", String(params.page));
  if (params.page_size) searchParams.append("page_size", String(params.page_size));

  // Handle is_active - can be boolean (old) or string (new from useServerDataTable)
  // Skip if value is "all" (means show all, don't filter)
  if (params.is_active !== undefined && params.is_active !== "all") {
    const isActiveValue =
      typeof params.is_active === "boolean" ? params.is_active : params.is_active === "true";
    searchParams.append("is_active", String(isActiveValue));
  }

  if (params.especialidad) searchParams.append("especialidad", String(params.especialidad));

  const url = searchParams.toString() ? `${MEDICOS_URL}?${searchParams}` : MEDICOS_URL;
  const response = await api.get<PaginatedResponse<MedicoRemitenteListItem>>(url);
  return response.data;
}

/**
 * Get a single médico remitente by ID
 */
export async function getMedicoRemitente(id: number): Promise<MedicoRemitente> {
  const response = await api.get<MedicoRemitente>(`${MEDICOS_URL}${id}/`);
  return response.data;
}

/**
 * Create a new médico remitente
 */
export async function createMedicoRemitente(
  data: MedicoRemitenteCreateData
): Promise<MedicoRemitente> {
  const response = await api.post<MedicoRemitente>(MEDICOS_URL, data);
  return response.data;
}

/**
 * Update an existing médico remitente
 */
export async function updateMedicoRemitente(
  id: number,
  data: MedicoRemitenteUpdateData
): Promise<MedicoRemitente> {
  const response = await api.patch<MedicoRemitente>(`${MEDICOS_URL}${id}/`, data);
  return response.data;
}

/**
 * Hard delete a médico remitente (permanent removal)
 */
export async function deleteMedicoRemitente(id: number): Promise<void> {
  await api.delete(`${MEDICOS_URL}${id}/`);
}

/**
 * Activate a médico remitente
 */
export async function activateMedicoRemitente(id: number): Promise<MedicoRemitente> {
  const response = await api.post<MedicoRemitente>(`${MEDICOS_URL}${id}/activate/`);
  return response.data;
}

/**
 * Deactivate a médico remitente (soft delete)
 */
export async function deactivateMedicoRemitente(id: number): Promise<MedicoRemitente> {
  const response = await api.post<MedicoRemitente>(`${MEDICOS_URL}${id}/deactivate/`);
  return response.data;
}

/**
 * Search médicos remitentes by name (for autocomplete/selectors)
 */
export async function buscarMedicosRemitentes(
  query: string,
  limit = 20
): Promise<MedicoRemitenteListItem[]> {
  const response = await api.get<PaginatedResponse<MedicoRemitenteListItem>>(MEDICOS_URL, {
    params: { search: query, page_size: limit, is_active: true },
  });
  return response.data.results;
}
